/**
 * Judge.me carousel: use 2nd product gallery image for fallback (lifestyle) when no review photo.
 */
(function () {
  const ROOT_SELECTOR = '.judgeme-reviews-showcase';
  const WIDGET_SELECTOR = '.judgeme-reviews-showcase__widget';
  const CARD_SELECTOR = '.jdgm-card, .jdgm-carousel-item';
  const ENHANCED_ATTR = 'data-jm-image-enhanced';
  const REVIEW_IMAGE_PATTERN = /judgeme\.imgix|judge\.me/i;

  /** @type {Map<string, Promise<{ images: string[] } | null>>} */
  const productRequests = new Map();

  /**
   * @param {string} src
   * @returns {boolean}
   */
  function isReviewPhoto(src) {
    return Boolean(src && REVIEW_IMAGE_PATTERN.test(src));
  }

  /**
   * @param {Element} card
   * @returns {string | null}
   */
  function getProductHandle(card) {
    const link = card.querySelector('.jdgm-product-name a[href*="/products/"]');
    if (!link || !(link instanceof HTMLAnchorElement)) return null;

    const match = link.pathname.match(/\/products\/([^/]+)/);
    return match ? match[1] : null;
  }

  /**
   * @param {string} handle
   * @returns {Promise<{ images: string[] } | null>}
   */
  function fetchProduct(handle) {
    if (productRequests.has(handle)) {
      return productRequests.get(handle);
    }

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    productRequests.set(handle, request);
    return request;
  }

  /**
   * @param {{ images?: string[] } | null} product
   * @returns {string | null}
   */
  function getPreferredImageUrl(product) {
    const images = product?.images;
    if (!images?.length) return null;
    return images[1] || images[0];
  }

  /**
   * @param {Element} card
   * @returns {HTMLImageElement | null}
   */
  function getCardImage(card) {
    for (const img of card.querySelectorAll('img')) {
      if (img instanceof HTMLImageElement && !isReviewPhoto(img.src)) {
        return img;
      }
    }
    return null;
  }

  /**
   * @param {Element} card
   * @returns {boolean}
   */
  function hasReviewPhoto(card) {
    for (const img of card.querySelectorAll('img')) {
      if (img instanceof HTMLImageElement && isReviewPhoto(img.src)) {
        return true;
      }
    }
    return false;
  }

  /**
   * @param {Element} card
   * @param {string} url
   */
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

  /**
   * @param {Element} card
   * @returns {HTMLElement | null}
   */
  function findCardMedia(card) {
    const selectors = [
      ':scope > [class*="product-image"]',
      ':scope > a[class*="product"]:has(img)',
      ':scope > img.judgeme-card__fallback-image',
      ':scope > img.jdgm-carousel-item__product-image',
      ':scope > img',
    ];

    for (const selector of selectors) {
      const el = card.querySelector(selector);
      if (el instanceof HTMLElement) return el;
    }

    return null;
  }

  /**
   * @param {Element} card
   * @returns {HTMLElement | null}
   */
  function getMobileStarsSlot(card) {
    let rating = card.querySelector(':scope > .jdgm-rating-section');
    if (!(rating instanceof HTMLElement)) {
      rating = card.querySelector('.jdgm-rating-section');
    }
    if (rating instanceof HTMLElement) return rating;

    const stars = card.querySelector('.jdgm-stars');
    if (!(stars instanceof HTMLElement)) return null;

    let slot = card.querySelector(':scope > .jm-mobile-stars');
    if (!(slot instanceof HTMLElement)) {
      slot = document.createElement('div');
      slot.className = 'jm-mobile-stars';
    }

    if (stars.parentElement !== slot || slot.lastElementChild !== stars) {
      slot.appendChild(stars);
    }
    return slot;
  }

  /**
   * Card order: stars → review → name → product → image
   * @param {Element} card
   */
  function reorderCard(card) {
    if (!(card instanceof HTMLElement)) return;

    const media = findCardMedia(card);
    const reviewer = card.querySelector('.jdgm-reviewer-info');
    const product = card.querySelector('.jdgm-product-name');
    const text = card.querySelector('.jdgm-text');

    if (reviewer instanceof HTMLElement && text?.contains(reviewer)) {
      card.appendChild(reviewer);
    }

    if (product instanceof HTMLElement && text?.contains(product)) {
      card.appendChild(product);
    }

    const stars = getMobileStarsSlot(card);
    const review = card.querySelector('.jdgm-text');
    const name = card.querySelector('.jdgm-reviewer-info');
    const productLine = card.querySelector('.jdgm-product-name');

    const orderedElements = [stars, review, name, productLine, media].filter(
      (el, index, elements) => el instanceof HTMLElement && elements.indexOf(el) === index
    );
    const cardChildren = [...card.children];
    const orderedTail = cardChildren.slice(-orderedElements.length);
    const alreadyOrdered = orderedElements.every((el, index) => orderedTail[index] === el);

    if (!alreadyOrdered) {
      orderedElements.forEach((el) => card.appendChild(el));
    }

    card.setAttribute('data-jm-card-order', 'stars-review-name-product');
  }

  function injectImage(card, url) {
    const img = document.createElement('img');
    img.className = 'judgeme-card__fallback-image';
    img.src = url;
    img.alt = '';
    img.loading = 'lazy';
    card.appendChild(img);
    reorderCard(card);
  }

  /**
   * @param {string} text
   * @returns {boolean}
   */
  function isAllCaps(text) {
    const letters = text.replace(/[^a-zA-Z]/g, '');
    return letters.length > 2 && letters === letters.toUpperCase();
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function toTitleCase(text) {
    return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function stripOuterQuotes(text) {
    return text.replace(/^[\s"'“”‘’«»]+|[\s"'“”‘’«»]+$/g, '').trim();
  }

  /**
   * @param {Element} card
   */
  function formatReviewQuote(card) {
    const textEl = card.querySelector('.jdgm-text');
    if (!(textEl instanceof HTMLElement)) return;

    const quote =
      textEl.querySelector('p:only-child') ||
      textEl.querySelector('.jdgm-rev__body p:first-child') ||
      textEl.querySelector('p:first-child');

    if (!(quote instanceof HTMLParagraphElement)) return;
    if (quote.classList.contains('jm-quote')) return;

    const raw = quote.textContent?.trim();
    if (!raw) return;

    quote.textContent = `\u201C${stripOuterQuotes(raw)}\u201D`;
    quote.classList.add('jm-quote');
  }

  /**
   * @param {Element} card
   */
  function formatProductName(card) {
    const link = card.querySelector('.jdgm-product-name a');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.dataset.jmTitleCased === 'true') return;

    const text = link.textContent?.trim();
    if (!text) return;

    if (isAllCaps(text)) {
      link.textContent = toTitleCase(text);
    }

    link.dataset.jmTitleCased = 'true';
  }

  /**
   * @param {Element} card
   */
  async function enhanceCard(card) {
    if (card.getAttribute(ENHANCED_ATTR) === 'true') return;

    const handle = getProductHandle(card);
    if (!handle) return;

    if (hasReviewPhoto(card)) {
      card.setAttribute(ENHANCED_ATTR, 'true');
      return;
    }

    const existingImg = getCardImage(card);

    const product = await fetchProduct(handle);
    const imageUrl = getPreferredImageUrl(product);
    if (!imageUrl) return;

    if (existingImg) {
      if (existingImg.src !== imageUrl) {
        existingImg.src = imageUrl;
        existingImg.removeAttribute('srcset');
      }
    } else {
      injectImage(card, imageUrl);
    }

    card.setAttribute(ENHANCED_ATTR, 'true');
    reorderCard(card);
  }

  /**
   * @param {string} seed
   * @returns {0 | 1 | 2}
   */
  function pickAvatarVariant(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash + seed.charCodeAt(i) * (i + 1)) | 0;
    }
    return Math.abs(hash) % 3;
  }

  /**
   * @param {Element} card
   * @returns {string}
   */
  function getAvatarSeed(card) {
    return (
      card.getAttribute('data-review-id') ||
      card.getAttribute('data-id') ||
      card.querySelector('.jdgm-reviewer-name')?.textContent?.trim() ||
      card.querySelector('.jdgm-text')?.textContent?.trim()?.slice(0, 48) ||
      ''
    );
  }

  /**
   * @param {HTMLImageElement} img
   * @returns {boolean}
   */
  function isRealReviewerPhoto(img) {
    const src = img.currentSrc || img.src;
    if (!src || src.startsWith('data:')) return false;
    return !/placeholder|default|blank|avatar\.svg/i.test(src);
  }

  /**
   * @param {Element} root
   */
  /* Avatars disabled for now — re-enable assignReviewerAvatars(root) in enhanceCards to restore. */
  function assignReviewerAvatars(root) {
    const avatarUrls = [
      root.getAttribute('data-jm-avatar-bun-url'),
      root.getAttribute('data-jm-avatar-waves-url'),
      root.getAttribute('data-jm-avatar-ponytail-url'),
    ];
    const customUrl = root.getAttribute('data-jm-avatar-custom-url');
    const useLuxe = root.getAttribute('data-jm-luxe-avatars') === 'true';

    if (!useLuxe && !customUrl) return;
    if (useLuxe && avatarUrls.some((url) => !url)) return;

    root.querySelectorAll('.jdgm-reviewer-info').forEach((info, index) => {
      if (!(info instanceof HTMLElement)) return;

      for (const img of info.querySelectorAll('img:not(.judgeme-reviewer-avatar)')) {
        if (img instanceof HTMLImageElement && isRealReviewerPhoto(img)) return;
        img.remove();
      }

      const misplaced = info.closest(CARD_SELECTOR)?.querySelector(
        ':scope > .judgeme-reviewer-avatar'
      );
      if (misplaced instanceof HTMLImageElement) misplaced.remove();

      let avatar = info.querySelector(':scope > .judgeme-reviewer-avatar');
      if (!(avatar instanceof HTMLImageElement)) {
        avatar = document.createElement('img');
        avatar.className = 'judgeme-reviewer-avatar';
        avatar.alt = '';
        avatar.setAttribute('width', '40');
        avatar.setAttribute('height', '40');
        avatar.loading = 'lazy';
        info.insertBefore(avatar, info.firstChild);
      }

      if (useLuxe) {
        const card = info.closest(CARD_SELECTOR);
        const seed = (card ? getAvatarSeed(card) : info.textContent?.trim()) || `review-${index}`;
        const variant = pickAvatarVariant(seed);
        avatar.src = avatarUrls[variant] || avatarUrls[0];
      } else if (customUrl) {
        avatar.src = customUrl;
      }
    });
  }

  /**
   * @param {Element} root
   */
  function enhanceCards(root) {
    limitReviewCards(root);

    const cards = root.querySelectorAll(CARD_SELECTOR);
    cards.forEach((card) => {
      formatProductName(card);
      formatReviewQuote(card);
      reorderCard(card);
      enhanceCard(card);
    });
  }

  const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');
  const MOBILE_MAX_REVIEWS = 8;
  const DESKTOP_MAX_REVIEWS = 12;
  const DESKTOP_CARDS_PER_VIEW = 4;
  const STORE_REVIEW_PRODUCT_NAME = 'endless & co';

  /**
   * @returns {number}
   */
  function getReviewLimit() {
    return DESKTOP_MQ.matches ? DESKTOP_MAX_REVIEWS : MOBILE_MAX_REVIEWS;
  }

  /**
   * @param {Element} card
   * @returns {boolean}
   */
  function isStoreReview(card) {
    const productName = card.querySelector('.jdgm-product-name')?.textContent;
    return productName?.replace(/\s+/g, ' ').trim().toLowerCase() === STORE_REVIEW_PRODUCT_NAME;
  }

  /**
   * @param {Element} root
   */
  function limitReviewCards(root) {
    const limit = getReviewLimit();
    let visibleIndex = 0;

    root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const skipStoreReview = isStoreReview(card);
      card.hidden = skipStoreReview || visibleIndex >= limit;
      if (!skipStoreReview) visibleIndex += 1;
    });
  }
  const DOTS_CLASS = 'judgeme-carousel-dots';
  const DOT_CLASS = 'judgeme-carousel-dots__dot';
  const ELLIPSIS_CLASS = 'judgeme-carousel-dots__ellipsis';
  const PROGRESS_CLASS = 'judgeme-carousel-progress';
  const PROGRESS_TRACK_CLASS = 'judgeme-carousel-progress__track';
  const PROGRESS_FILL_CLASS = 'judgeme-carousel-progress__fill';
  const ACTIVE_CLASS = 'is-active';
  const MAX_VISIBLE_DOTS = 5;

  /**
   * @returns {boolean}
   */
  function isDesktopPagination() {
    return DESKTOP_MQ.matches;
  }

  /**
   * @param {Element} widget
   * @returns {HTMLElement | null}
   */
  function getScrollTrack(widget) {
    const candidates = [
      widget.querySelector('.jdgm-videos-container'),
      widget.querySelector('.jdgm-cards-wrapper'),
    ];

    for (const el of candidates) {
      if (el instanceof HTMLElement && el.scrollWidth > el.clientWidth + 1) {
        return el;
      }
    }

    const fallback = widget.querySelector('.jdgm-videos-container, .jdgm-cards-wrapper');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {HTMLElement} track
   * @returns {HTMLElement[]}
   */
  function getVisibleCards(track) {
    return [...track.querySelectorAll(CARD_SELECTOR)].filter(
      (card) => card instanceof HTMLElement && !card.hidden
    );
  }

  /**
   * Use actual card positions rather than deriving them from width + gap.
   * Judge.me adds card margins and clones, so a calculated step drifts on mobile.
   * @param {HTMLElement} track
   * @param {number} pageIndex
   * @returns {number}
   */
  function getPageScrollLeft(track, pageIndex) {
    const cards = getVisibleCards(track);
    const cardIndex = DESKTOP_MQ.matches ? pageIndex * DESKTOP_CARDS_PER_VIEW : pageIndex;
    const card = cards[cardIndex];
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

    if (!(card instanceof HTMLElement)) return maxScroll;
    if (cardIndex >= cards.length - (DESKTOP_MQ.matches ? DESKTOP_CARDS_PER_VIEW : 1)) {
      return maxScroll;
    }

    return Math.min(maxScroll, Math.max(0, card.offsetLeft));
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  function getDesktopPageStep(track) {
    const cards = [...track.querySelectorAll('.jdgm-card, .jdgm-carousel-item')].filter(
      (card) => card instanceof HTMLElement && !card.hidden
    );
    if (!cards.length) return getCardStep(track);

    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 12;
    const cardWidth = cards[0].offsetWidth;
    return cardWidth * DESKTOP_CARDS_PER_VIEW + gap * (DESKTOP_CARDS_PER_VIEW - 1);
  }

  /**
   * @param {Element} widget
   */
  function applyDesktopLayout(widget) {
    if (!DESKTOP_MQ.matches) return;

    const track = getScrollTrack(widget);
    if (!(track instanceof HTMLElement)) return;

    track.scrollLeft = 0;
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  function getCardStep(track) {
    if (DESKTOP_MQ.matches) return getDesktopPageStep(track);

    const card = track.querySelector('.jdgm-card, .jdgm-carousel-item');
    if (!(card instanceof HTMLElement)) return 0;

    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 12;
    return card.offsetWidth + gap;
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  function getPageCount(track) {
    const visibleCards = getVisibleCards(track);

    if (DESKTOP_MQ.matches) {
      return Math.max(1, Math.ceil(visibleCards.length / DESKTOP_CARDS_PER_VIEW));
    }

    if (visibleCards.length) return visibleCards.length;

    const step = getCardStep(track);
    if (!step) return 0;

    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 1) return 1;

    return Math.ceil(maxScroll / step) + 1;
  }

  /**
   * @param {Element} widget
   * @returns {HTMLElement | null}
   */
  function findNativePagination(widget) {
    const selectors = [
      '.jdgm-bottom-pagination',
      '.jdgm-carousel__dots',
      '.jdgm-carousel-dots',
      '.jdgm-pagination',
      '[class*="jdgm"][class*="pagination"]',
    ];

    for (const selector of selectors) {
      const el = widget.querySelector(selector);
      if (el instanceof HTMLElement && !el.classList.contains('jdgm-arrow')) {
        return el;
      }
    }

    return null;
  }

  /**
   * @param {Element} widget
   * @param {HTMLElement} pagination
   */
  function placePaginationBelowContent(widget, pagination) {
    const content = widget.querySelector('.jdgm-content');
    if (content && pagination.parentElement !== widget) {
      widget.appendChild(pagination);
      return;
    }

    if (content && pagination.previousElementSibling !== content) {
      content.insertAdjacentElement('afterend', pagination);
    }
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  function getActivePageIndex(track) {
    const pageCount = getPageCount(track);
    if (pageCount <= 1) return 0;

    let activeIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
      const distance = Math.abs(track.scrollLeft - getPageScrollLeft(track, pageIndex));
      if (distance < nearestDistance) {
        nearestDistance = distance;
        activeIndex = pageIndex;
      }
    }

    return activeIndex;
  }

  /**
   * @param {number} pageCount
   * @param {number} activeIndex
   * @returns {number[]}
   */
  function getVisiblePageIndices(pageCount, activeIndex) {
    if (pageCount <= MAX_VISIBLE_DOTS) {
      return Array.from({ length: pageCount }, (_, i) => i);
    }

    const indices = new Set([0, pageCount - 1]);
    for (let i = activeIndex - 2; i <= activeIndex + 2; i += 1) {
      if (i >= 0 && i < pageCount) indices.add(i);
    }

    return [...indices].sort((a, b) => a - b);
  }

  /**
   * @param {HTMLElement} dots
   * @param {HTMLElement} track
   */
  function updateActiveDot(dots, track) {
    const activeIndex = getActivePageIndex(track);

    dots.querySelectorAll(`.${DOT_CLASS}`).forEach((button) => {
      const pageIndex = Number.parseInt(button.dataset.pageIndex || '0', 10);
      const isActive = pageIndex === activeIndex;
      button.classList.toggle(ACTIVE_CLASS, isActive);
      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  /**
   * @param {HTMLElement} dots
   * @param {HTMLElement} track
   * @param {number} pageCount
   */
  function renderDotControls(dots, track, pageCount) {
    const activeIndex = getActivePageIndex(track);
    const indices = getVisiblePageIndices(pageCount, activeIndex);
    const signature = indices.join(',');

    if (dots.dataset.jmVisibleSignature === signature) {
      updateActiveDot(dots, track);
      return;
    }

    dots.dataset.jmVisibleSignature = signature;
    dots.replaceChildren();

    let prev = -1;
    for (const pageIndex of indices) {
      if (prev >= 0 && pageIndex - prev > 1) {
        const ellipsis = document.createElement('span');
        ellipsis.className = ELLIPSIS_CLASS;
        ellipsis.setAttribute('aria-hidden', 'true');
        ellipsis.textContent = '…';
        dots.appendChild(ellipsis);
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = DOT_CLASS;
      button.dataset.pageIndex = String(pageIndex);
      button.setAttribute('aria-label', `Go to review page ${pageIndex + 1}`);
      button.addEventListener('click', () => {
        const left = getPageScrollLeft(track, pageIndex);
        track.scrollTo({ left, behavior: 'smooth' });
      });
      dots.appendChild(button);
      prev = pageIndex;
    }

    updateActiveDot(dots, track);
  }

  /**
   * @param {HTMLElement} progress
   * @param {HTMLElement} track
   */
  function updateProgressBar(progress, track) {
    const fill = progress.querySelector(`.${PROGRESS_FILL_CLASS}`);
    if (!(fill instanceof HTMLElement)) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const percent = maxScroll <= 1 ? 100 : (track.scrollLeft / maxScroll) * 100;
    fill.style.width = `${Math.min(100, Math.max(0, percent))}%`;
    progress.setAttribute('aria-valuenow', String(Math.round(percent)));
  }

  /**
   * @param {Element} widget
   * @param {HTMLElement} track
   */
  function buildProgressBar(widget, track) {
    const pageCount = getPageCount(track);
    let progress = widget.querySelector(`nav.${PROGRESS_CLASS}`);

    if (pageCount <= 1) {
      if (progress instanceof HTMLElement) progress.hidden = true;
      return;
    }

    if (!(progress instanceof HTMLElement)) {
      progress = document.createElement('nav');
      progress.className = PROGRESS_CLASS;
      progress.setAttribute('aria-label', 'Reviews carousel progress');
      progress.setAttribute('role', 'progressbar');
      progress.setAttribute('aria-valuemin', '0');
      progress.setAttribute('aria-valuemax', '100');

      const trackEl = document.createElement('div');
      trackEl.className = PROGRESS_TRACK_CLASS;
      const fill = document.createElement('div');
      fill.className = PROGRESS_FILL_CLASS;
      trackEl.appendChild(fill);
      progress.appendChild(trackEl);

      trackEl.addEventListener('click', (event) => {
        const rect = trackEl.getBoundingClientRect();
        const ratio = (event.clientX - rect.left) / rect.width;
        const maxScroll = track.scrollWidth - track.clientWidth;
        track.scrollTo({ left: ratio * maxScroll, behavior: 'smooth' });
      });

      placePaginationBelowContent(widget, progress);
    }

    progress.hidden = false;
    updateProgressBar(progress, track);

    if (!track.dataset.jmProgressBound) {
      track.dataset.jmProgressBound = 'true';
      track.addEventListener(
        'scroll',
        () => {
          const nav = widget.querySelector(`nav.${PROGRESS_CLASS}`);
          if (nav instanceof HTMLElement) updateProgressBar(nav, track);
        },
        { passive: true }
      );
    }
  }

  /**
   * @param {Element} widget
   * @param {HTMLElement} track
   */
  function buildCustomDots(widget, track) {
    const pageCount = getPageCount(track);
    let dots = widget.querySelector(`nav.${DOTS_CLASS}`);

    if (pageCount <= 1) {
      if (dots instanceof HTMLElement) dots.hidden = true;
      return;
    }

    if (!(dots instanceof HTMLElement)) {
      dots = document.createElement('nav');
      dots.className = DOTS_CLASS;
      dots.setAttribute('aria-label', 'Reviews carousel pages');
      placePaginationBelowContent(widget, dots);
    }

    dots.hidden = false;
    renderDotControls(dots, track, pageCount);

    if (!track.dataset.jmDotsBound) {
      track.dataset.jmDotsBound = 'true';
      track.addEventListener(
        'scroll',
        () => {
          const nav = widget.querySelector(`nav.${DOTS_CLASS}`);
          if (!(nav instanceof HTMLElement)) return;
          renderDotControls(nav, track, getPageCount(track));
        },
        { passive: true }
      );
    }
  }

  /**
   * @param {Element} widget
   */
  function initPagination(widget) {
    const track = getScrollTrack(widget);
    const native = findNativePagination(widget);

    if (native) {
      native.classList.add(DOTS_CLASS);
      placePaginationBelowContent(widget, native);
      native.hidden = true;
    }

    const progress = widget.querySelector(`nav.${PROGRESS_CLASS}`);
    const dots = widget.querySelector(`nav.${DOTS_CLASS}`);

    if (!track) {
      if (progress instanceof HTMLElement) progress.hidden = true;
      if (dots instanceof HTMLElement) dots.hidden = true;
      return;
    }

    if (!track.dataset.jmScrollInitialized) {
      track.dataset.jmScrollInitialized = 'true';
      track.scrollLeft = getPageScrollLeft(track, 0);
    }

    if (isDesktopPagination()) applyDesktopLayout(widget);
    if (progress instanceof HTMLElement) progress.hidden = true;
    buildCustomDots(widget, track);
  }

  let debounceTimer = 0;
  let paginationTimer = 0;

  function init() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    const widget = root.querySelector(WIDGET_SELECTOR);
    if (!widget) return;

    const carousel = widget.querySelector('.jdgm-cards-carousel') || widget;

    enhanceCards(root);

    const schedulePagination = () => {
      window.clearTimeout(paginationTimer);
      paginationTimer = window.setTimeout(() => initPagination(carousel), 150);
    };

    schedulePagination();

    const observer = new MutationObserver(() => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        enhanceCards(root);
        schedulePagination();
      }, 100);
    });

    observer.observe(widget, { childList: true, subtree: true });

    const onResize = () => {
      limitReviewCards(root);
      schedulePagination();
      applyDesktopLayout(carousel);
    };

    const onCardLayout = () => {
      root.querySelectorAll(CARD_SELECTOR).forEach((card) => reorderCard(card));
    };

    window.addEventListener('resize', onResize, { passive: true });
    DESKTOP_MQ.addEventListener('change', onResize);
    MOBILE_MQ.addEventListener('change', onCardLayout);
    onCardLayout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
